import { Button } from '@/components/common/inputs/button/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/common/inputs/text/form';
import { Checkbox } from '@/components/common/inputs/checkbox/checkbox';

import { Input } from '@/components/common/inputs/text/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/common/overlay/dialog/dialog';
import {
  Language,
  LanguageSelector,
} from '@/components/features/i18n/LanguageSelector';
import { useToast } from '@/lib/hooks/useToast';
import { userService } from '@/lib/services';
import { userState } from '@/lib/store/user.store';
import { useI18n } from '@/locales/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { observer, use$, useObservable } from '@legendapp/state/react';
import { useSession } from 'next-auth/react';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const UserInfoSchema = z.object({
  city: z.string().min(1, 'City is required').optional(),
  ageGroup: z.enum(['18-25', '26-35', '36-50', '51+']).optional(),
  language: z.enum(['en', 'fr']).optional(),
  acceptedPolicies: z.boolean(),
  role: z.enum(['user', 'lawyer']),
});

export type UserInfoFormData = z.infer<typeof UserInfoSchema>;

interface FormState {
  city: string | undefined;
  ageGroup: UserInfoFormData['ageGroup'] | undefined;
  acceptedPolicies: UserInfoFormData['acceptedPolicies'];
  language: UserInfoFormData['language'] | undefined;
  role: UserInfoFormData['role'];
}

interface UserInfoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProfileUpdate?: () => void;
}

const isValidValue = (value: string | null | undefined): boolean => {
  return value != null && value !== 'na' && value !== '';
};

export const UserInfoDialog = observer(
  ({ open, onOpenChange, onProfileUpdate }: UserInfoDialogProps) => {
    const t = useI18n();
    const { data: session } = useSession();
    const { toast } = useToast();
    const user = use$(userState?.profile?.data);

    // Create local observable state that syncs with userState
    const formState = useObservable<FormState>({
      city: isValidValue(user?.city) ? user?.city : '',
      ageGroup: isValidValue(user?.ageGroup)
        ? (user?.ageGroup as FormState['ageGroup'])
        : undefined,
      language: isValidValue(user?.language)
        ? (user?.language as FormState['language'])
        : 'en',
      role: user?.role as FormState['role'],
      acceptedPolicies: !!user?.acceptedPolicies, // Ensure it's a boolean
    });

    // Create form with observable state
    const form = useForm<UserInfoFormData>({
      resolver: zodResolver(UserInfoSchema),
      defaultValues: {
        city: formState.city.get() || undefined,
        ageGroup: formState.ageGroup.get(),
        acceptedPolicies: formState.acceptedPolicies.get(),
        language: formState.language.get(),
        role: formState.role.get(),
      },
    });

    // Sync form with user state changes
    useEffect(() => {
      if (user) {
        const newState = {
          city: isValidValue(user.city) ? user.city : '',
          ageGroup: isValidValue(user.ageGroup)
            ? (user.ageGroup as FormState['ageGroup'])
            : undefined,
          language: isValidValue(user.language)
            ? (user.language as FormState['language'])
            : 'en',
          role: user.role as FormState['role'],
          acceptedPolicies: !!user?.acceptedPolicies,
        };
        formState.set(newState);
        form.reset({
          city: newState.city || undefined,
          ageGroup: newState.ageGroup,
          acceptedPolicies: newState.acceptedPolicies,
          language: newState.language,
          role: newState.role,
        });
      }
    }, [user, form, formState]);

    // Handle language selection
    const handleSelectLanguage = useCallback(
      (language: Language) => {
        const newLang = language.key as FormState['language'];
        formState.language.set(newLang);
        form.setValue('language', newLang);
      },
      [form, formState]
    );

    // Submit handler
    const onSubmitUserInfo = async (data: UserInfoFormData) => {
      console.log('[onSubmitUserInfo]');
      try {
        if (session?.user?.id) {
          toast({
            title: 'Updating profile...',
            description: 'Please wait while we save your changes.',
          });
          console.log(
            '[onSubmitUserInfo]: userService.updateProfile using: ',
            data
          );
          await userService.updateUser(
            session.user.id,
            {
              city: data.city || 'na',
              ageGroup: data.ageGroup || 'na',
              acceptedPolicies: data.acceptedPolicies || false,
              language: data.language || 'en',
              role: data.role,
            },
            session
          );
          console.log(
            'acceptedPolicies in userState.profile.data',
            userState.profile.data.acceptedPolicies.get()
          );
          // Update user state
          userState.profile.data.city.set(data.city || 'na');
          userState.profile.data.ageGroup.set(data.ageGroup || 'na');
          userState.profile.data.acceptedPolicies.set(
            data.acceptedPolicies || false
          );
          userState.profile.data.language.set(data.language || 'fr');
          userState.profile.data.role.set(data.role);
          onOpenChange(false);
          onProfileUpdate?.();

          toast({
            title: 'Success',
            description: 'Profile updated successfully',
          });
        }
      } catch (error) {
        console.error('Error updating user info:', error);
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Failed to update profile',
        });
      }
    };

    const languageKey = formState.language.get();
    const languageName = languageKey === 'en' ? 'English' : 'French';

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className='sm:max-w-[425px]'>
          <DialogTitle className='text-lg font-semibold'>
            {t('register.profile.title')}
          </DialogTitle>
          <DialogDescription className='text-sm text-gray-500'>
            {t('register.profile.description')}
          </DialogDescription>
          <div className='grid gap-4 py-4'>
            <Form {...form}>
              <div className='space-y-4'>
                <FormField
                  control={form.control}
                  name='city'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('register.city')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value || ''}
                          onChange={(e) => {
                            field.onChange(e);
                            formState.city.set(e.target.value);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='ageGroup'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('register.ageGroup')}</FormLabel>
                      <FormControl>
                        <select
                          {...field}
                          value={field.value || ''}
                          className='w-full rounded-md border border-gray-300 px-2 py-1'
                          onChange={(e) => {
                            const value = e.target.value || undefined;
                            field.onChange(value);
                            formState.ageGroup.set(
                              value as FormState['ageGroup']
                            );
                          }}
                        >
                          <option value=''>Select</option>
                          <option value='18-25'>18-25</option>
                          <option value='26-35'>26-35</option>
                          <option value='36-50'>36-50</option>
                          <option value='51+'>51+</option>
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormItem>
                  <FormLabel>{t('register.languages.title')}</FormLabel>
                  <FormControl>
                    <LanguageSelector
                      onSelectLanguage={handleSelectLanguage}
                      selectedLanguage={{
                        key: languageKey || 'fr',
                        name: languageName,
                      }}
                      className='w-full'
                    />
                  </FormControl>
                </FormItem>
                <FormField
                  control={form.control}
                  name='role'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('register.role.question')}</FormLabel>
                      <FormControl>
                        <select
                          {...field}
                          value={field.value || ''}
                          className='w-full rounded-md border border-gray-300 px-2 py-1'
                          onChange={(e) => {
                            const value = e.target.value || undefined;
                            field.onChange(value);
                            formState.role.set(value as FormState['role']);
                          }}
                        >
                          <option value='' disabled>
                            {t('register.role.placeholder')}
                          </option>
                          <option value='user'>
                            {t('register.role.user')}
                          </option>
                          <option value='lawyer'>
                            {t('register.role.lawyer')}
                          </option>
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='acceptedPolicies'
                  render={({ field }) => (
                    <FormItem className='flex flex-col space-y-2'>
                      <div className='flex items-center space-x-2'>
                        <Checkbox
                          id='terms'
                          checked={field.value}
                          onCheckedChange={(checked) => {
                            const value = !!checked;
                            field.onChange(value);
                            formState.acceptedPolicies.set(
                              value as FormState['acceptedPolicies']
                            );
                            console.log('acceptedPolicies updated to:', value);
                          }}
                        />
                        <div className='px-4 py-2 text-center text-sm text-muted-foreground'>
                          {t('policies.acceptTermsPolicies.text_1')}{' '}
                          <a
                            href={'/policies?tab=privacy'}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='m-0 inline cursor-pointer border-none bg-transparent p-0 text-primary underline hover:text-primary/80'
                          >
                            {t('policies.acceptTermsPolicies.policy')}{' '}
                          </a>
                          {t('policies.acceptTermsPolicies.text_2')}{' '}
                          <a
                            href={'/policies?tab=terms'}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='m-0 inline cursor-pointer border-none bg-transparent p-0 text-primary underline hover:text-primary/80'
                          >
                            {t('policies.acceptTermsPolicies.termsOfUse')}
                          </a>
                          .
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type='submit'
                  onClick={form.handleSubmit(onSubmitUserInfo)}
                  className='w-full'
                >
                  {t('register.profile.update')}
                </Button>
              </div>
            </Form>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
);
